import { CdkDrag, CdkDragHandle } from '@angular/cdk/drag-drop';
import { input, signal, Component, output, inject, ChangeDetectionStrategy } from '@angular/core';
import { toObservable } from '@angular/core/rxjs-interop';

import { switchMap } from 'rxjs';
import { filter, startWith } from 'rxjs/operators';

import { I18nService, TranslatePipe } from '@core/i18n-legacy';
import {
  RtTooltip,
  IconButtonComponent,
  GroupComponent,
  IconComponent,
  IconColorDirective,
  ButtonDropdownIndicatorDirective,
  ButtonComponent,
  DropdownScrollableComponent,
  ConnectedOverlayDirective,
  OverlayOriginDirective,
  type IconName,
  DropdownComponent,
  DropdownItemComponent,
  DropdownWidth,
} from '@core/user-interface-legacy';
import { injectUntilDestroyed } from '@core/utils-legacy';

import { DynamicFormConfigService } from '../../../dynamic-form-config.service';
import { noopFormGroupTransform } from '../../../utils';

export type MoveEvent = 'up' | 'down';

@Component({
  selector: 'rt-define-form-wrapper',
  templateUrl: './define-form-wrapper.component.html',
  styleUrls: ['./define-form-wrapper.component.less'],
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [
    ConnectedOverlayDirective,
    DropdownScrollableComponent,
    ButtonComponent,
    ButtonDropdownIndicatorDirective,
    OverlayOriginDirective,
    IconColorDirective,
    IconComponent,
    GroupComponent,
    IconButtonComponent,
    RtTooltip,
    TranslatePipe,
    DropdownComponent,
    DropdownItemComponent,
    CdkDragHandle,
    CdkDrag,
  ],
})
export class DefineFormWrapperComponent {
  private readonly dynamicFormConfigService = inject(DynamicFormConfigService);
  private readonly i18n = inject(I18nService);
  private readonly untilDestroyed = injectUntilDestroyed();

  protected readonly DropdownWidth = DropdownWidth;
  public hasError = signal(false);

  public defineForm = input.required({
    transform: noopFormGroupTransform<{ required: boolean } | {}>,
  });
  public respondKindIcon = input.required<IconName>();
  public respondKindName = input.required<string>();

  public move = output<MoveEvent>();
  public delete = output();
  public duplicate = output();
  public changeTemplate = output<string>();

  protected defineFormControlItems = this.dynamicFormConfigService.defineAvailableConfigs;
  protected requiredOptions = {
    required: {
      label: this.i18n.translate('tlc.dynamic_form.define.required'),
      icon: 'asterisk',
    },
    optional: {
      label: this.i18n.translate('tlc.dynamic_form.define.optional'),
      icon: 'call-split',
    },
  } satisfies Record<string, { label: string; icon: IconName }>;

  constructor() {
    this.trackFormChanges();
  }

  public toggleRequired(required: boolean): void {
    this.defineForm().patchValue({ required });
  }

  private trackFormChanges(): void {
    toObservable(this.defineForm)
      .pipe(
        switchMap(form => form.statusChanges.pipe(startWith(form.status))),
        filter(status => status === 'VALID'),
        this.untilDestroyed,
      )
      .subscribe(() => {
        this.hasError.set(false);
      });
  }
}
